import React, { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, Box, Typography, IconButton, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ButtonOne from "../../common/normal-button";
import ButtonThree from "../../common/third-button";
import { ColorsConstants, borderColors } from "../../cssUtils/constants";
import typography from "../../cssUtils/typography";
import { useAuth } from "../../hooks/AuthProvider";
import { PHARMACY_EMPLOYEES_ENDPOINT } from "../../config/urls";

export default function EmployeeQrModal({ employee, onClose, onGenerated }) {
  const { getValidToken } = useAuth();
  const [current, setCurrent] = useState(employee);
  const [loading, setLoading] = useState(!employee.qr_code_url);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (current.qr_code_url) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getValidToken();
        const tenant = localStorage.getItem("tenant");
        const res = await fetch(`${PHARMACY_EMPLOYEES_ENDPOINT}${employee.id}/generate-qr/`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "X-Tenant-Id": tenant },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Failed to generate QR code.");
        if (!cancelled) {
          setCurrent(data);
          onGenerated?.(data);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || "Failed to generate QR code.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCopy = async () => {
    if (!current.wa_link) return;
    try {
      await navigator.clipboard.writeText(current.wa_link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard may be unavailable — link is still visible/selectable in the box below
    }
  };

  const handleDownload = () => {
    if (!current.qr_code_url) return;
    const a = document.createElement("a");
    a.href = current.qr_code_url;
    a.download = `${employee.employee_id}_qr.png`;
    a.target = "_blank";
    a.rel = "noreferrer";
    a.click();
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Box>
          <Typography sx={{ ...typography.h2 }}>{employee.employee_name}</Typography>
          <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.primaryGray50 }}>
            {employee.employee_id}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1.5, pb: 3 }}>
        <Box
          sx={{
            display: "flex", justifyContent: "center", alignItems: "center",
            bgcolor: ColorsConstants.primaryBlue60, borderRadius: 2, p: 3, minHeight: 220,
          }}
        >
          {loading ? (
            <CircularProgress size={32} />
          ) : current.qr_code_url ? (
            <img
              src={current.qr_code_url}
              alt={`QR code for ${employee.employee_name}`}
              width={200}
              height={200}
              style={{ borderRadius: 4 }}
            />
          ) : (
            <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.error, textAlign: "center" }}>
              {error || "QR code not available."}
            </Typography>
          )}
        </Box>

        {error && current.qr_code_url && (
          <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.error }}>{error}</Typography>
        )}

        <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.primaryGray50 }}>
          Scanning this opens WhatsApp with this message pre-filled:
        </Typography>
        <Box
          sx={{
            ...typography.bodySmall,
            fontFamily: "monospace",
            color: ColorsConstants.primaryGray60,
            bgcolor: ColorsConstants.primaryBlue60,
            border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
            borderRadius: 1,
            px: 1.5, py: 1,
            wordBreak: "break-all",
          }}
        >
          {current.wa_link || "—"}
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, mt: 1 }}>
          <ButtonOne
            title="Download"
            variant="contained"
            startIcon={<DownloadIcon fontSize="small" />}
            disabled={!current.qr_code_url}
            onClick={handleDownload}
            sx={{ flex: 1 }}
          />
          <ButtonThree
            startIcon={<ContentCopyIcon fontSize="small" />}
            onClick={handleCopy}
            disabled={!current.wa_link}
            sx={{ flex: 1 }}
          >
            {copied ? "Copied" : "Copy link"}
          </ButtonThree>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
